import { create } from "zustand";

// The acting-company context — spark's implementation of
// the company account model ("Profil-Switch"):
//
//   "die Person sieht ihre Firmen (aus ihren `company_membership`-Zeilen) und schaltet in eine
//    hinein. Technisch = kein Re-Login, kein neues Token, gleicher `sub`; die aktive `companyId`
//    ist ein UI-/Anzeigekontext, den Genesis-Mutations optional mitbekommen und gegen die
//    Membership (Ebene B) autorisieren. Keycloak sieht vom Switch nichts."
//
// THIS IS THE FIRST STATE IN SPARK THAT IS NOT A UI PREFERENCE, TRANSIENT FEATURE STATE OR A
// READ-ONLY SESSION MIRROR, so three rules exist that no other store here needs, and each is
// enforced somewhere a test can see it:
//
// 1. IT IS AN ID, NOT A COMPANY. Nothing renders from this value directly. `useCompanyContext`
//    resolves it against the `myCompanies` list genesis returns and produces a context only when
//    the server's own membership rows contain it — so the name on screen is always genesis's
//    answer, never the browser's. An id the caller has no membership for resolves to nothing and
//    is cleared.
//
// 2. IT AUTHORIZES NOTHING. Every per-company mutation carries its target company id from the
//    route or the page it was invoked on, and genesis re-checks a `company_membership` row with
//    `role = admin` for that target on every call (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization). The BFF proxy does no
//    authorization at all — it injects the bearer and forwards verbatim — so anything the browser
//    holds is attacker-controlled by construction. `use-company-admin.ts` deliberately does not
//    import this file.
//
//    AMENDED BY CONCEPT v23 (Kevin, 2026-09-11, extended 2026-09-12). This value used to say it
//    "must never become an argument to a write". It now IS one: a write carries `actingAsCompanyId`
//    on the request, and the declared context and the ownership of the content must MATCH. Company
//    content — its comment, post or project — needs a declaration naming exactly that company, and a
//    write with none, or with another company's id, is refused (GENESIS001259). The caller's OWN
//    content needs NO declaration, and one is refused too (GENESIS001260) — "if someone acts as a
//    company, he cannot edit/delete/create comments as private person". Creating is inside the rule
//    as well, though a create may still omit the declaration and be private. The context is therefore
//    EXCLUSIVE: two modes, each with its own subject matter, neither reaching into the other.
//
//    Read the sentence that survives, because it is the whole of the amendment: the value still
//    AUTHORIZES NOTHING. genesis re-checks the `company_membership` admin row exactly as before and
//    never trusts this id in its place; the declaration only says which context the caller believes
//    they are acting in, and every principal who may write can send it. So v23 stops a person
//    editing their company's words while in personal mode — and their own words while in the
//    company's — and stops nothing else. Kevin accepted that knowingly; genesis#300 carries the
//    three shapes a real boundary would need. Do not read the new argument as a permission, and do
//    not remove the membership check because this value is present.
//
// 3. CHANGING IT RESETS THE APOLLO CACHE. One normalized cache keyed by entity id, deduped across
//    consumers by design, would otherwise serve an entity read while acting as company A to a
//    reader acting as company B. The policy and its reasoning live in
//    ../graphql/company-context-cache.ts, which is where it is wired.
//
// NOT PERSISTED, deliberately. §5 asks for a display context and says nothing about outliving the
// tab's JS heap; keeping it in memory means there is no durable browser artifact whose only job is
// decoration, nothing to clear on logout, and no id from a previous session resolving against a
// fresh membership list. Re-entering costs one click from /c or from the company's own page.

interface CompanyContextState {
  /** The company the person is acting as, or `null` for their own person context. */
  activeCompanyId: string | null;
  enterCompany: (companyId: string) => void;
  leaveCompany: () => void;
}

export const useCompanyContextStore = create<CompanyContextState>()((set) => ({
  activeCompanyId: null,
  enterCompany: (companyId) => set({ activeCompanyId: companyId }),
  leaveCompany: () => set({ activeCompanyId: null }),
}));
